import {
  CdkDrag,
  CdkDragDrop,
  CdkDragPreview,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { NgClass } from '@angular/common';
import {
  Component,
  computed,
  inject,
  input,
  OnDestroy,
  signal,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import * as fromSeriousSystem from '@serious-system';
import { generateKeyBetween } from 'fractional-indexing';
import * as fromGenerated from '../../../_generated';
import * as fromHelper from '../../../analytics/analytics.helper';
import { AnalyticsService } from '../../../analytics/analytics.service';
import * as fromFavorites from '../../../favorites';
import * as fromFeatureFlags from '../../../feature-flags';
import * as fromLayout from '../../../layout';
import * as fromAssistants from '../../assistants';
import { RouterService } from '../../shared/services/router.service';

const MOBILE_DRAG_START_DELAY_MS = 250;

export type NavigationFavorite = Pick<
  fromGenerated.AssistantView,
  'uuid' | 'name' | 'logo'
> & {
  sortOrder: string;
};

@Component({
  selector: 'squadbox-navigation-favorites-list-smart',
  imports: [
    TranslateModule,
    fromAssistants.AssistantLogoComponent,
    CdkDropList,
    CdkDrag,
    CdkDragPreview,
    fromSeriousSystem.UseUIBasicIconDirective,
    NgClass,
  ],
  template: `
    <div class="flex flex-col gap-1">
      <!-- Favorites Separator -->
      <div class="navigation-label">
        {{ 'MAIN_NAVIGATION_ITEMS.MY_FAVORITES' | translate }}
      </div>
      <!-- Favorites List -->
      <div
        cdkDropList
        class="flex flex-col gap-1"
        (cdkDropListDropped)="drop($event)"
      >
        @for (favorite of favoritesList(); track favorite.uuid) {
          <div
            cdkDrag
            role="button"
            tabindex="0"
            [cdkDragDisabled]="!ffReorderingMyAssistantFavorites()"
            [cdkDragStartDelay]="dragStartDelay()"
            [cdkDragBoundary]="
              isMobile() ? '.cdk-drop-list' : 'squadbox-chats-desktop'
            "
            class="navigation-item group"
            [ngClass]="{
              'mobile-drag-held': isMobileDragHeld(favorite.uuid),
            }"
            (touchstart)="handleMobileEvent($event, favorite.uuid)"
            (mousedown)="handleMobileEvent($event, favorite.uuid)"
            (touchend)="handleMobileEvent($event)"
            (mouseup)="handleMobileEvent($event)"
            (click)="handleClickFavoriteCTA(favorite.uuid)"
            (keydown.enter)="handleClickFavoriteCTA(favorite.uuid)"
            (keydown.space)="
              handleClickFavoriteCTA(favorite.uuid); $event.preventDefault()
            "
          >
            <!-- Custom Drag Preview (follows mouse) -->
            <div *cdkDragPreview>
              <div
                class="
                  flex items-center gap-2
                  px-3 py-2 max-w-64
                  text-shades-white bg-primary-500 rounded-sm
                "
              >
                <squadbox-assistant-logo
                  class="
                  text-shades-white
                  [&>svg,img]:size-5
                  [&>svg,img]:shrink-0
                "
                  [logo]="favorite.logo"
                />
                <span class="text-sm font-semibold overflow-hidden truncate">
                  {{ favorite.name }}
                </span>
              </div>
            </div>
            <!-- Drag Handle -->
            @if (ffReorderingMyAssistantFavorites()) {
              <div
                class="
                  cursor-grab
                  w-0 group-hover:w-5.5
                  opacity-0 group-hover:opacity-100
                  transition-[width,opacity] duration-400 ease-in-out
                "
                [ngClass]="{
                  'w-5.5 opacity-100':
                    !isMobile() && isJustDropped(favorite.uuid),
                }"
              >
                <svg
                  sdUseUIBasicIcon="drag"
                  class="size-4 text-neutral-400"
                ></svg>
              </div>
            }
            <div class="flex flex-row items-center gap-2 flex-1 min-w-0">
              <!-- Favorite Logo -->
              <squadbox-assistant-logo
                class="
                [&>svg,img]:text-primary-300
                [&>svg,img]:size-6 tablet-portrait:[&>svg,img]:size-5
                [&>svg,img]:shrink-0
              "
                [logo]="favorite.logo"
              />
              <!-- Favorite Name -->
              <span class="font-semibold overflow-hidden truncate">
                {{ favorite.name }}
              </span>
            </div>
          </div>
        }
      </div>
    </div>
  `,
})
export class NavigationFavoritesListSmartComponent implements OnDestroy {
  private readonly routerService = inject(RouterService);
  private readonly featureFlagsStore = inject(
    Store<fromFeatureFlags.FeatureFlagsState>,
  );
  private readonly layoutStore = inject(Store<fromLayout.LayoutState>);
  private readonly favoritesStore = inject(
    Store<fromFavorites.AssistantFavoritesState>,
  );
  private readonly assistantsStore = inject(
    Store<fromAssistants.AssistantsState>,
  );
  private readonly analyticsService = inject(AnalyticsService);

  public readonly favoritesList = input.required<NavigationFavorite[]>();

  private readonly allFavorites = this.favoritesStore.selectSignal(
    fromFavorites.assistantFavoritesFeature.selectFavoritesSorted,
  );

  public readonly isMobile = this.layoutStore.selectSignal(
    fromLayout.layoutFeature.selectIsMobile,
  );

  private readonly assistantEntities = this.assistantsStore.selectSignal(
    fromAssistants.assistantsFeature.selectEntities,
  );

  public readonly ffReorderingMyAssistantFavorites =
    this.featureFlagsStore.selectSignal(
      fromFeatureFlags.featureFlagsFeature.selectFeatureFlag(
        'ff-reordering-my-favourites-assistants',
      ),
    );

  public async handleClickFavoriteCTA(uuid: string) {
    const assistant = this.assistantEntities()[uuid];
    if (assistant) {
      this.analyticsService
        .sendSkillOpenedEvent(
          fromHelper.toSkillOpenedEvent(assistant, 'favorites_navigation'),
        )
        .subscribe();
    } else {
      this.analyticsService
        .sendSkillOpenedEvent({
          assistantId: uuid,
          source: 'favorites_navigation',
        })
        .subscribe();
    }
    await this.routerService.navigateToNewChatWithSkill(uuid);
  }

  public readonly dragStartDelay = computed(() =>
    this.isMobile() ? MOBILE_DRAG_START_DELAY_MS : 0,
  );

  public drop(event: CdkDragDrop<NavigationFavorite[]>) {
    if (event.previousIndex === event.currentIndex) {
      return;
    }
    const currentList = this.favoritesList();
    const newList = [...currentList];

    moveItemInArray(newList, event.previousIndex, event.currentIndex);

    const movedItem = newList[event.currentIndex];
    // Mark the dropped item to show hover state immediately
    this.markAsJustDropped(movedItem.uuid);

    // Neighbors come from the full sorted list: a favorite hidden from the
    // visible list (the default skill, KD-12) still holds a key, and a key
    // computed from visible neighbors alone can land on it.
    const prevItem =
      event.currentIndex > 0 ? newList[event.currentIndex - 1] : null;
    const otherFavorites = this.allFavorites().filter(
      (favorite) => favorite.assistantId !== movedItem.uuid,
    );
    const nextFavorite = prevItem
      ? (otherFavorites[
          otherFavorites.findIndex(
            (favorite) => favorite.assistantId === prevItem.uuid,
          ) + 1
        ] ?? null)
      : (otherFavorites[0] ?? null);

    const newSortOrder = generateKeyBetween(
      prevItem?.sortOrder ?? null,
      nextFavorite?.sortOrder ?? null,
    );

    this.favoritesStore.dispatch(
      fromFavorites.AssistantFavoritesActions.updateSortOrder({
        assistantId: movedItem.uuid,
        sortOrder: newSortOrder,
        previousSortOrder: movedItem.sortOrder,
      }),
    );
  }

  // Track which item is being held (showing visual feedback after hold delay)
  private readonly heldDragUuid = signal<string | null>(null);
  private feedbackTimeout: ReturnType<typeof setTimeout> | null = null;

  // Track which item was just dropped (to show hover state immediately)
  private readonly justDroppedUuid = signal<string | null>(null);
  private justDroppedTimeout: ReturnType<typeof setTimeout> | null = null;

  public readonly isMobileDragEnabled = computed(
    () => this.isMobile() && this.ffReorderingMyAssistantFavorites(),
  );

  private cancelMobileDragFeedback() {
    if (this.feedbackTimeout) {
      clearTimeout(this.feedbackTimeout);
      this.feedbackTimeout = null;
    }
    this.heldDragUuid.set(null);
  }

  // Centralized mobile event handler for drag-n-drop
  public handleMobileEvent(
    event: TouchEvent | MouseEvent,
    uuid?: string,
  ): void {
    if (!this.isMobileDragEnabled()) return;

    // Handle TouchEvent/MouseEvent
    switch (event.type) {
      case 'touchstart':
      case 'mousedown':
        if (uuid) {
          this.cancelMobileDragFeedback();
          // Show visual feedback after user holds for the delay period
          this.feedbackTimeout = setTimeout(() => {
            this.heldDragUuid.set(uuid);
          }, MOBILE_DRAG_START_DELAY_MS);
        }
        break;

      case 'touchend':
      case 'mouseup':
        this.cancelMobileDragFeedback();
        break;
    }
  }

  public isMobileDragHeld(uuid: string): boolean {
    return this.isMobileDragEnabled() && this.heldDragUuid() === uuid;
  }

  private markAsJustDropped(uuid: string): void {
    // Clear any existing timeout
    if (this.justDroppedTimeout) {
      clearTimeout(this.justDroppedTimeout);
    }
    // Mark as just dropped
    this.justDroppedUuid.set(uuid);
    // Remove the flag after a short delay (longer than hover transition)
    this.justDroppedTimeout = setTimeout(() => {
      this.justDroppedUuid.set(null);
      this.justDroppedTimeout = null;
    }, 500); // Slightly longer than the 400ms transition
  }

  public isJustDropped(uuid: string): boolean {
    return this.justDroppedUuid() === uuid;
  }

  ngOnDestroy() {
    this.cancelMobileDragFeedback();
    if (this.justDroppedTimeout) {
      clearTimeout(this.justDroppedTimeout);
    }
  }
}
